import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Truck, Boxes, FileSpreadsheet, FileText, X, LayoutTemplate, History as HistoryIcon, Share2, Palette, Download } from "lucide-react";
import FileUpload from "@/components/FileUpload";
import CargoTable from "@/components/CargoTable";
import VehicleConfig from "@/components/VehicleConfig";
import AllocationResult from "@/components/AllocationResult";
import HistoryPanel from "@/components/HistoryPanel";
import TemplatesPanel from "@/components/TemplatesPanel";
import TripDetails from "@/components/TripDetails";
import TripSheet from "@/components/TripSheet";
import AppHeader from "@/components/AppHeader";
import CalculateBar from "@/components/CalculateBar";
import BrandSettingsDialog from "@/components/BrandSettingsDialog";
import CoverPageDialog from "@/components/CoverPageDialog";
import AuthDialog from "@/components/AuthDialog";
import OnboardingDialog from "@/components/OnboardingDialog";
import { Button } from "@/components/ui/button";
import {
  extractCases,
  calculateLoad,
  getDefaultVehicles,
  getExportDefaultVehicles,
  saveCalculation,
  saveTemplate,
  downloadExcel,
  downloadPdf,
  createSharedPlan,
  sharedPlanUrl,
  fetchUserBrand,
  saveUserBrand,
} from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { useAuth } from "@/lib/auth";

const EMPTY_TRIP = {
  from_location: "",
  to_location: "",
  trip_type: "One Way",
  driver_name: "",
  driver_phone: "",
  vehicle_number: "",
  scheduled_at: "",
};

// LocalStorage key + helper for the transporter's custom brand (logo + tagline + website).
// Kept client-side so it survives across sessions without user accounts.
const BRAND_STORAGE_KEY = "cargoload_pro_brand_v1";
const EMPTY_BRAND = {
  logo_data_url: "",
  company_name: "",
  tagline: "",
  address: "",
  contact: "",
  website: "",
};
function loadBrand() {
  try {
    const raw = localStorage.getItem(BRAND_STORAGE_KEY);
    return raw ? { ...EMPTY_BRAND, ...JSON.parse(raw) } : EMPTY_BRAND;
  } catch { return EMPTY_BRAND; }
}
function saveBrand(brand) {
  try { localStorage.setItem(BRAND_STORAGE_KEY, JSON.stringify(brand)); } catch { /* quota */ }
}
function hasBrandData(brand) {
  if (!brand) return false;
  return !!(brand.logo_data_url || (brand.company_name && brand.company_name.trim()));
}

// Bright yellow strip above the results — nudges users to white-label their
// exports. Only rendered when there's a result AND no brand has been saved.
function BrandNudgeBanner({ user, onOpenBrand, onLogin, onSignup }) {
  return (
    <div
      data-testid="brand-nudge-banner"
      className="mb-6 border-l-4 border-yellow-400 bg-yellow-50 rounded-r-md px-4 md:px-6 py-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-6 shadow-sm"
    >
      <div className="flex items-start md:items-center gap-3 flex-1">
        <div className="w-10 h-10 shrink-0 bg-yellow-400 text-slate-900 font-bold flex items-center justify-center rounded-md mono">
          !
        </div>
        <div>
          <div className="heading text-lg font-semibold text-slate-900">
            Your logo isn't set yet.
          </div>
          <div className="text-sm text-slate-600 mt-0.5">
            Add your company logo so every PDF and Excel export goes out on <span className="text-slate-900 font-semibold">your</span> letterhead — not ours.
            {!user && (
              <>
                {" "}<span className="text-slate-900 font-semibold">Sign up</span> to save it across every device.
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex gap-2 shrink-0 flex-wrap">
        <button
          type="button"
          data-testid="brand-nudge-open-brand"
          onClick={onOpenBrand}
          className="mono text-xs uppercase tracking-widest bg-yellow-400 hover:bg-yellow-300 text-slate-900 font-bold px-4 h-10 rounded-md transition-colors"
        >
          Add Your Logo →
        </button>
        {!user && (
          <>
            <button
              type="button"
              data-testid="brand-nudge-signup"
              onClick={onSignup}
              className="mono text-xs uppercase tracking-widest border border-[#2563EB] bg-[#2563EB] text-white hover:bg-[#1D4ED8] px-4 h-10 rounded-md transition-colors"
            >
              Sign Up
            </button>
            <button
              type="button"
              data-testid="brand-nudge-login"
              onClick={onLogin}
              className="mono text-xs uppercase tracking-widest border border-slate-300 bg-white text-slate-700 hover:border-[#2563EB] hover:text-[#2563EB] px-4 h-10 rounded-md transition-colors"
            >
              Login
            </button>
          </>
        )}
      </div>
    </div>
  );
}


function useCargoSummary(cases) {
  return useMemo(() => {
    const totalWeight = cases.reduce((s, c) => s + c.weight_kg * (c.quantity || 1), 0);
    const totalVolume = cases.reduce(
      (s, c) => s + (c.length_cm * c.width_cm * c.height_cm * (c.quantity || 1)) / 1_000_000,
      0
    );
    const totalUnits = cases.reduce((s, c) => s + (c.quantity || 1), 0);
    return { totalWeight, totalVolume, totalUnits };
  }, [cases]);
}

function HeroUpload({ onFile, loading }) {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">
      <div className="lg:col-span-1">
        <div className="overline mb-3">Step 01 — Upload</div>
        <h1 className="heading text-4xl md:text-5xl font-bold mb-4 leading-[1.05]">
          Turn packing lists into <span className="text-[#2563EB]">load plans</span>.
        </h1>
        <p className="text-[#64748B] text-sm leading-relaxed max-w-md">
          Upload a photo, screenshot, or PDF of your client's packing list. Our AI reads case
          dimensions and weights, then calculates the exact fleet of 40ft trailers, 32ft trucks,
          and hydraulic axles you need.
        </p>
        <div className="mt-6 space-y-2 mono text-xs text-[#64748B]">
          <div className="flex items-center gap-2"><span className="text-[#2563EB]">→</span> Supports JPG · PNG · PDF · Excel (.xls / .xlsx)</div>
          <div className="flex items-center gap-2"><span className="text-[#2563EB]">→</span> Edit extracted data before calculating</div>
          <div className="flex items-center gap-2"><span className="text-[#2563EB]">→</span> Downloads to Excel — ready for clients</div>
        </div>
      </div>
      <div className="lg:col-span-2">
        <FileUpload onFile={onFile} loading={loading} />
      </div>
    </section>
  );
}

function CargoAndFleetSection({ cases, setCases, vehicles, setVehicles, summary, onSaveTemplate, sourceDimUnit, sourceWeightUnit }) {
  const [showFleet, setShowFleet] = useState(false);
  return (
    <section className="mb-10 fade-up">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline">Step 02 — Cargo</div>
          <h2 className="heading text-2xl font-semibold mt-1 flex items-center gap-2">
            <Boxes className="w-5 h-5 text-[#2563EB]" /> Cargo Cases
          </h2>
        </div>
        <div className="flex items-center gap-3">
          <div className="mono text-xs text-[#64748B] hidden md:flex items-center gap-4">
            <span>{summary.totalUnits} units</span>
            <span>{summary.totalWeight.toLocaleString(undefined, { maximumFractionDigits: 0 })} kg</span>
            <span>{summary.totalVolume.toFixed(2)} m³</span>
          </div>
          {cases.length > 0 && (
            <Button
              data-testid="save-template-btn"
              variant="ghost"
              onClick={onSaveTemplate}
              className="rounded-md border border-slate-200 text-slate-700 hover:text-[#2563EB] hover:bg-slate-100 mono uppercase text-[10px] tracking-widest"
              title="Save the current cargo + fleet + trip as a reusable template"
            >
              <LayoutTemplate className="w-3.5 h-3.5 mr-1.5" /> Save as Template
            </Button>
          )}
        </div>
      </div>
      <CargoTable
        cases={cases}
        onChange={setCases}
        sourceDimUnit={sourceDimUnit}
        sourceWeightUnit={sourceWeightUnit}
      />

      {/* Fleet configuration — hidden by default. Most users go with the default fleet;
          power users open this to tweak vehicle specs. */}
      <div className="mt-6">
        <button
          type="button"
          data-testid="toggle-fleet-btn"
          onClick={() => setShowFleet((v) => !v)}
          className="mono text-[11px] uppercase tracking-widest text-slate-500 hover:text-[#2563EB] border border-slate-200 px-3 py-2 flex items-center gap-2 transition-colors"
          aria-expanded={showFleet}
        >
          <Truck className="w-3.5 h-3.5" />
          {showFleet ? "Hide Fleet Configuration" : "Configure Vehicle Fleet (optional)"}
          <span className="text-[#2563EB] ml-1">{showFleet ? "−" : "+"}</span>
        </button>
        {showFleet && (
          <div className="mt-3 fade-up" data-testid="fleet-config-panel">
            <div className="mb-3">
              <div className="overline">Step 03 — Fleet (optional)</div>
              <h3 className="heading text-lg font-semibold mt-1 flex items-center gap-2">
                <Truck className="w-4 h-4 text-[#2563EB]" /> Vehicle Fleet
              </h3>
              <p className="mono text-[11px] text-[#64748B] mt-1">
                The defaults cover 95% of Indian transporter fleets. Only change these if you have custom trucks.
              </p>
            </div>
            <VehicleConfig vehicles={vehicles} onChange={setVehicles} />
          </div>
        )}
      </div>
    </section>
  );
}

function ResultSection({ result, tripSheet, dimUnit, onDimUnitChange, weightUnit, onWeightUnitChange, tableLayout, onTableLayoutChange, coverEnabled, onOpenCover, onSave, onDownload, onDownloadPdf, onShare, onOpenBrand }) {
  return (
    <section id="result-section" className="fade-up">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <div className="overline">Step 04 — Load Plan</div>
          <h2 className="heading text-2xl font-semibold mt-1">Vehicle Allocation</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Dimension unit toggle — flipped on the fly, reflected on exports */}
          <div className="inline-flex border border-slate-200 h-9" data-testid="dim-unit-toggle">
            <span className="mono text-[10px] uppercase tracking-widest text-slate-500 self-center px-2 border-r border-slate-200">
              L·W·H
            </span>
            <button
              type="button"
              data-testid="dim-unit-mm"
              onClick={() => onDimUnitChange("mm")}
              className={
                "px-2.5 mono text-[10px] uppercase tracking-widest transition-colors " +
                (dimUnit === "mm"
                  ? "bg-[#2563EB] text-white font-semibold"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100")
              }
            >
              MM
            </button>
            <button
              type="button"
              data-testid="dim-unit-cm"
              onClick={() => onDimUnitChange("cm")}
              className={
                "px-2.5 mono text-[10px] uppercase tracking-widest transition-colors border-l border-slate-200 " +
                (dimUnit === "cm"
                  ? "bg-[#2563EB] text-white font-semibold"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100")
              }
            >
              CM
            </button>
          </div>
          {/* Weight unit toggle */}
          <div className="inline-flex border border-slate-200 h-9" data-testid="weight-unit-toggle">
            <span className="mono text-[10px] uppercase tracking-widest text-slate-500 self-center px-2 border-r border-slate-200">
              WT
            </span>
            <button
              type="button"
              data-testid="weight-unit-kg"
              onClick={() => onWeightUnitChange("kg")}
              className={
                "px-2.5 mono text-[10px] uppercase tracking-widest transition-colors " +
                (weightUnit === "kg"
                  ? "bg-[#2563EB] text-white font-semibold"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100")
              }
            >
              KG
            </button>
            <button
              type="button"
              data-testid="weight-unit-mt"
              onClick={() => onWeightUnitChange("mt")}
              className={
                "px-2.5 mono text-[10px] uppercase tracking-widest transition-colors border-l border-slate-200 " +
                (weightUnit === "mt"
                  ? "bg-[#2563EB] text-white font-semibold"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100")
              }
            >
              MT
            </button>
          </div>
          {/* Table layout toggle — collapses long plans into one flat table */}
          <div className="inline-flex border border-slate-200 h-9" data-testid="table-layout-toggle">
            <span className="mono text-[10px] uppercase tracking-widest text-slate-500 self-center px-2 border-r border-slate-200">
              Layout
            </span>
            <button
              type="button"
              data-testid="layout-auto"
              onClick={() => onTableLayoutChange("auto")}
              className={
                "px-2 mono text-[10px] uppercase tracking-widest transition-colors " +
                (tableLayout === "auto"
                  ? "bg-[#2563EB] text-white font-semibold"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100")
              }
              title="Per-vehicle blocks; auto-flatten at 20+ vehicles"
            >
              Auto
            </button>
            <button
              type="button"
              data-testid="layout-flat"
              onClick={() => onTableLayoutChange("flat")}
              className={
                "px-2 mono text-[10px] uppercase tracking-widest transition-colors border-l border-slate-200 " +
                (tableLayout === "flat"
                  ? "bg-[#2563EB] text-white font-semibold"
                  : "text-slate-600 hover:text-slate-900 hover:bg-slate-100")
              }
              title="One consolidated table across all vehicles"
            >
              Flat
            </button>
          </div>
          <Button
            data-testid="open-brand-btn"
            variant="ghost"
            className="rounded-md border border-slate-200 hover:bg-slate-100 hover:text-[#2563EB] flex-1 md:flex-none"
            onClick={onOpenBrand}
            title="Upload your own logo + tagline for the export header"
          >
            <Palette className="w-4 h-4 mr-2" /> Brand
          </Button>
          <Button
            data-testid="open-cover-btn"
            variant="ghost"
            className={
              "rounded-md border flex-1 md:flex-none " +
              (coverEnabled
                ? "border-[#2563EB] bg-[#2563EB]/10 text-[#2563EB] hover:bg-[#2563EB]/15"
                : "border-slate-200 hover:bg-slate-100 hover:text-[#2563EB]")
            }
            onClick={onOpenCover}
            title="Add a client-ready cover page to the PDF (client name, validity, signature)"
          >
            <FileText className="w-4 h-4 mr-2" /> Cover {coverEnabled ? "· ON" : ""}
          </Button>
          <Button
            data-testid="share-plan-btn"
            variant="ghost"
            className="rounded-md border border-slate-200 hover:bg-slate-100 hover:text-[#2563EB] flex-1 md:flex-none"
            onClick={onShare}
            title="Get a read-only shareable link"
          >
            <Share2 className="w-4 h-4 mr-2" /> Share Link
          </Button>
          <Button
            data-testid="save-history-btn"
            variant="ghost"
            className="rounded-md border border-slate-200 hover:bg-slate-100 hover:text-[#2563EB] flex-1 md:flex-none"
            onClick={onSave}
          >
            Save to History
          </Button>
          <Button
            data-testid="download-pdf-btn"
            variant="ghost"
            onClick={onDownloadPdf}
            className="rounded-md border border-slate-200 text-white hover:bg-slate-100 hover:text-[#2563EB] mono uppercase tracking-widest text-xs flex-1 md:flex-none"
            title="Download a print-ready PDF of the loading plan"
          >
            <FileText className="w-4 h-4 mr-2" /> Download PDF
          </Button>
          <Button
            data-testid="download-excel-btn"
            onClick={onDownload}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white rounded-md font-semibold mono uppercase tracking-widest text-xs transition-colors duration-200 flex-1 md:flex-none"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" /> Download Excel
          </Button>
          <a
            data-testid="download-brochure-toolbar"
            href="/marketing/cargoload-onepager.pdf"
            target="_blank"
            rel="noreferrer"
            className="rounded-md border border-slate-200 text-slate-600 hover:bg-slate-100 hover:text-[#2563EB] mono uppercase tracking-widest text-[10px] h-10 px-3 inline-flex items-center transition-colors flex-1 md:flex-none justify-center"
            title="Download the CargoLoad Pro one-pager brochure"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" /> Brochure
          </a>
        </div>
      </div>
      <AllocationResult result={result} tripSheet={tripSheet} />
    </section>
  );
}

function DrawerTab({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={
        "flex-1 px-4 py-3 mono text-[11px] uppercase tracking-widest transition-colors flex items-center justify-center gap-2 " +
        (active
          ? "bg-white/[0.04] text-[#2563EB] border-b-2 border-[#2563EB]"
          : "text-slate-500 hover:text-slate-900 border-b-2 border-transparent")
      }
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

function HistoryDrawer({ show, refreshKey, templatesRefreshKey, initialTab, onClose, onLoad, onLoadTemplate }) {
  const [tab, setTab] = useState(initialTab || "history");
  useEffect(() => {
    if (show && initialTab) setTab(initialTab);
  }, [show, initialTab]);

  if (!show) return null;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/25 backdrop-blur" onClick={onClose}>
      <div
        className="absolute right-0 top-0 h-full w-full md:w-[520px] bg-[#FFFFFF] border-l border-slate-200 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-[#FFFFFF] z-10">
          <div>
            <div className="overline">Archive</div>
            <div className="heading text-xl font-semibold">
              {tab === "templates" ? "Templates" : "Calculation History"}
            </div>
          </div>
          <button
            data-testid="close-history-btn"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900 transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex border-b border-slate-200 bg-[#FFFFFF] sticky top-[81px] z-10">
          <DrawerTab
            active={tab === "history"}
            onClick={() => setTab("history")}
            icon={HistoryIcon}
            label="History"
            testId="drawer-tab-history"
          />
          <DrawerTab
            active={tab === "templates"}
            onClick={() => setTab("templates")}
            icon={LayoutTemplate}
            label="Templates"
            testId="drawer-tab-templates"
          />
        </div>
        {tab === "history" ? (
          <HistoryPanel refreshKey={refreshKey} onLoad={onLoad} />
        ) : (
          <TemplatesPanel refreshKey={templatesRefreshKey} onLoad={onLoadTemplate} />
        )}
      </div>
    </div>
  );
}

export default function CargoLoadPro() {
  const [mode, setMode] = useState("domestic"); // "domestic" | "export"
  const [cases, setCases] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [result, setResult] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [optimizeMinTrucks, setOptimizeMinTrucks] = useState(false);
  const [jobName, setJobName] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const [drawerTab, setDrawerTab] = useState("history");
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const [templatesRefreshKey, setTemplatesRefreshKey] = useState(0);
  const [trip, setTrip] = useState(EMPTY_TRIP);
  const [brand, setBrand] = useState(() => loadBrand());
  const [showBrandDialog, setShowBrandDialog] = useState(false);
  const [authDialog, setAuthDialog] = useState({ show: false, tab: "signup" });
  const openSignup = () => setAuthDialog({ show: true, tab: "signup" });
  const openLogin = () => setAuthDialog({ show: true, tab: "login" });
  const closeAuth = () => setAuthDialog((d) => ({ ...d, show: false }));
  const [showOnboarding, setShowOnboarding] = useState(false);
  const handleFirstSignup = () => setShowOnboarding(true);
  const finishOnboarding = async (nextBrand) => {
    setBrand(nextBrand);
    saveBrand(nextBrand);
    try {
      await saveUserBrand(nextBrand);
    } catch { /* offline — localStorage still has it */ }
    setShowOnboarding(false);
    toast.success("You're all set. Every export will use your brand.");
  };
  const skipOnboarding = () => setShowOnboarding(false);
  const { user, loading: authLoading } = useAuth();
  // When a user signs in, hydrate their brand from the server (once).
  // When they save/edit while signed-in, persist to the server too.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const serverBrand = await fetchUserBrand();
        if (cancelled) return;
        // Only overwrite local state if the server actually has data — otherwise
        // we'd blow away brand the user just typed in localStorage.
        if (hasBrandData(serverBrand)) {
          const merged = { ...EMPTY_BRAND, ...serverBrand };
          setBrand(merged);
          saveBrand(merged);
        } else if (hasBrandData(brand)) {
          // First-time sign-in with local brand data — push it up so future
          // devices auto-load it.
          await saveUserBrand(brand);
        }
      } catch { /* offline or 401 — leave local state alone */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);
  // Client-ready cover page (PDF only). Persisted so the client name / notes
  // survive across sessions if the transporter often quotes the same buyer.
  const [coverPage, setCoverPage] = useState(() => {
    try {
      const raw = localStorage.getItem("cargoload_cover_page_v1");
      return raw ? JSON.parse(raw) : { enabled: false, client_name: "", quote_validity: "", notes: "" };
    } catch { return { enabled: false, client_name: "", quote_validity: "", notes: "" }; }
  });
  const [showCoverDialog, setShowCoverDialog] = useState(false);
  const handleSaveCover = (next) => {
    setCoverPage(next);
    try { localStorage.setItem("cargoload_cover_page_v1", JSON.stringify(next)); } catch { /* quota */ }
  };
  // Dimension unit shown on downloaded plans — MM (matches packing-list style
  // e.g. 4000×2960×1890 mm) or CM (industry-standard for Indian transporters).
  // Persisted so we don't ask again on every session.
  const [dimUnit, setDimUnit] = useState(() => {
    try { return localStorage.getItem("cargoload_dim_unit_v1") || "cm"; } catch { return "cm"; }
  });
  const handleDimUnitChange = (u) => {
    setDimUnit(u);
    try { localStorage.setItem("cargoload_dim_unit_v1", u); } catch { /* quota */ }
  };
  // Weight unit shown on downloaded plans — KG (matches most packing lists) or MT.
  const [weightUnit, setWeightUnit] = useState(() => {
    try { return localStorage.getItem("cargoload_weight_unit_v1") || "kg"; } catch { return "kg"; }
  });
  const handleWeightUnitChange = (u) => {
    setWeightUnit(u);
    try { localStorage.setItem("cargoload_weight_unit_v1", u); } catch { /* quota */ }
  };
  // Table layout: "auto" (per-vehicle blocks; auto-flatten at 20+), "flat"
  // (one consolidated table across all vehicles), or "per_vehicle" (always
  // banner + per-vehicle table). Persisted so long-plan users don't reset.
  const [tableLayout, setTableLayout] = useState(() => {
    try { return localStorage.getItem("cargoload_table_layout_v1") || "auto"; } catch { return "auto"; }
  });
  const handleTableLayoutChange = (t) => {
    setTableLayout(t);
    try { localStorage.setItem("cargoload_table_layout_v1", t); } catch { /* quota */ }
  };
  // Source units detected on the last upload — drives the "Source units"
  // dropdowns on the cargo table so a wrong guess is one click away from fixed.
  const [sourceUnits, setSourceUnits] = useState({ dim: "cm", weight: "kg" });

  useEffect(() => {
    const loader = mode === "export" ? getExportDefaultVehicles : getDefaultVehicles;
    loader()
      .then(setVehicles)
      .catch(() => toast.error(`Failed to load ${mode === "export" ? "shipping containers" : "default vehicles"}`));
    // Reset the result when the mode changes — old allocations aren't valid for the new fleet.
    setResult(null);
  }, [mode]);

  const summary = useCargoSummary(cases);

  const handleExtract = async (file) => {
    setExtracting(true);
    setResult(null);
    trackEvent("upload_start", {
      file_ext: file.name.split(".").pop()?.toLowerCase() || "unknown",
      size_kb: Math.round(file.size / 1024),
      mode,
    });
    try {
      toast.info("Extracting cargo data from " + file.name + "…");
      const resp = await extractCases(file);
      const extracted = resp?.cases || [];
      if (!extracted.length) {
        toast.error("No cargo cases detected. You can add them manually below.");
        trackEvent("upload_no_cases", { mode });
      } else {
        toast.success(`Extracted ${extracted.length} cases`);
        trackEvent("upload_success", { cases: extracted.length, mode });
        // Auto-apply detected units to the export toggles so downloads match
        // the source packing list.
        if (resp.detected_dimension_unit && resp.detected_dimension_unit !== dimUnit) {
          handleDimUnitChange(resp.detected_dimension_unit);
          toast.info(`Detected dimensions in ${resp.detected_dimension_unit.toUpperCase()}`);
        }
        if (resp.detected_weight_unit && resp.detected_weight_unit !== weightUnit) {
          handleWeightUnitChange(resp.detected_weight_unit);
          toast.info(`Detected weight in ${resp.detected_weight_unit.toUpperCase()}`);
        }
        setSourceUnits({
          dim: resp.source_dimension_unit || "cm",
          weight: resp.source_weight_unit || "kg",
        });
        if (resp.estimated_weight_count > 0) {
          toast.info(
            `${resp.estimated_weight_count} row(s) had no weight — estimated at 250 kg/CBM (editable)`
          );
        }
      }
      setCases(extracted);
      if (!jobName) setJobName(file.name.replace(/\.[^/.]+$/, ""));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extraction failed");
      trackEvent("upload_error", { mode });
    } finally {
      setExtracting(false);
    }
  };

  const handleCalculate = async () => {
    if (!cases.length) {
      toast.error("Add at least one cargo case first");
      return;
    }
    setCalculating(true);
    // Dimensions-only rows → nominal volumetric weight (250 kg/CBM, Indian road
    // freight norm) so the table shows exactly what the engine will plan with.
    let estimated = 0;
    const prepared = cases.map((c) => {
      if (!c.weight_kg || Number(c.weight_kg) <= 0) {
        const vol = (c.length_cm * c.width_cm * c.height_cm) / 1_000_000;
        if (vol > 0) {
          estimated += 1;
          return { ...c, weight_kg: Math.round(vol * 250 * 10) / 10, weight_estimated: true };
        }
      }
      return c;
    });
    if (estimated > 0) {
      setCases(prepared);
      toast.info(`${estimated} case(s) had no weight — planned at nominal 250 kg/CBM`);
    }
    trackEvent("calculate_start", {
      cases: cases.length,
      units: cases.reduce((s, c) => s + (c.quantity || 1), 0),
      mode,
    });
    try {
      const res = await calculateLoad(prepared, vehicles, { optimizeMinTrucks });
      setResult(res);
      if (res.unassigned?.length) {
        toast.warning(`${res.unassigned.length} case(s) could not be assigned`);
      } else {
        toast.success(`Calculated: ${res.summary.total_vehicles} vehicle(s) needed`);
      }
      const autoOdc = res.optimization_summary?.auto_generated_vehicles || 0;
      if (autoOdc > 0) {
        toast.warning(
          `${autoOdc} over-dimensional piece(s) routed to auto-sized ODC rigs — RTO permit required`
        );
      }
      trackEvent("calculate_success", {
        vehicles: res.summary.total_vehicles || 0,
        unassigned: res.unassigned?.length || 0,
        mode,
      });
      setTimeout(() => {
        document.getElementById("result-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Calculation failed");
      trackEvent("calculate_error", { mode });
    } finally {
      setCalculating(false);
    }
  };

  const handleDownload = async () => {
    if (!result) return;
    trackEvent("download_excel", {
      vehicles: result.summary?.total_vehicles || 0,
      mode,
    });
    try {
      await downloadExcel({
        name: jobName || "Cargo Load Plan",
        result,
        cases,
        vehicles,
        trip,
        brand,
        dimension_unit: dimUnit,
        weight_unit: weightUnit,
        table_layout: tableLayout,
      });
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const handleDownloadPdf = async () => {
    if (!result) return;
    trackEvent("download_pdf", {
      vehicles: result.summary?.total_vehicles || 0,
      mode,
    });
    try {
      await downloadPdf({
        name: jobName || "Cargo Load Plan",
        result,
        cases,
        vehicles,
        trip,
        brand,
        cover_page: coverPage,
        dimension_unit: dimUnit,
        weight_unit: weightUnit,
        table_layout: tableLayout,
      });
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("PDF download failed");
    }
  };

  const handleShare = async () => {
    if (!result) return;
    try {
      const rec = await createSharedPlan({
        name: jobName || "Cargo Load Plan",
        trip,
        brand,
        cases,
        vehicles,
        result,
      });
      const url = sharedPlanUrl(rec.token);
      // Copy to clipboard (best effort — never throw).
      try { await navigator.clipboard?.writeText(url); } catch { /* clipboard blocked */ }
      trackEvent("share_created", { mode, vehicles: result.summary?.total_vehicles || 0 });
      toast.success("Share link copied — open on any phone", {
        description: url,
        duration: 8000,
      });
    } catch (e) {
      toast.error("Could not create share link");
    }
  };

  const handleSaveBrand = (next) => {
    setBrand(next);
    saveBrand(next);
    setShowBrandDialog(false);
    if (user) {
      // Fire-and-forget — dialog toast already confirmed the save.
      saveUserBrand(next).catch(() => {});
    }
  };

  const handleSave = async () => {
    if (!result) return;
    try {
      await saveCalculation({
        name: jobName || "Untitled",
        cases,
        vehicles,
        result,
      });
      toast.success("Calculation saved to history");
      setHistoryRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const handleSaveTemplate = async () => {
    if (!cases.length) {
      toast.error("Add at least one cargo case to save as template");
      return;
    }
    const suggested = jobName || "Untitled Template";
    // eslint-disable-next-line no-alert
    const name = window.prompt("Template name:", suggested);
    if (name === null) return; // user cancelled
    const finalName = name.trim() || suggested;
    try {
      await saveTemplate({
        name: finalName,
        mode,
        cases,
        vehicles,
        trip,
      });
      toast.success(`Template "${finalName}" saved`);
      setTemplatesRefreshKey((k) => k + 1);
    } catch (e) {
      toast.error("Failed to save template");
    }
  };

  const openTemplates = () => {
    setDrawerTab("templates");
    setShowHistory(true);
  };

  const openHistory = () => {
    setDrawerTab("history");
    setShowHistory(true);
  };

  const loadFromTemplate = (tmpl) => {
    // Switch mode if template was saved in a different one
    if (tmpl.mode && tmpl.mode !== mode) {
      setMode(tmpl.mode);
    }
    setCases(tmpl.cases || []);
    setVehicles(tmpl.vehicles || []);
    setTrip(tmpl.trip || EMPTY_TRIP);
    setJobName(tmpl.name || "");
    setResult(null);
    setShowHistory(false);
    toast.info(`Template loaded: ${tmpl.name}`);
  };

  const loadFromHistory = (record) => {
    setCases(record.cases || []);
    setVehicles(record.vehicles || []);
    setResult(record.result || null);
    setJobName(record.name || "");
    setTrip(record.trip || EMPTY_TRIP);
    setShowHistory(false);
    toast.info("Loaded: " + record.name);
  };

  const resetAll = () => {
    setCases([]);
    setResult(null);
    setJobName("");
    setTrip(EMPTY_TRIP);
    const loader = mode === "export" ? getExportDefaultVehicles : getDefaultVehicles;
    loader().then(setVehicles);
  };

  const handleModeChange = (next) => {
    if (mode === next) return;
    setMode(next);
    setCases([]);
  };

  return (
    <div className="min-h-screen relative">
      <AppHeader
        mode={mode}
        onModeChange={handleModeChange}
        onOpenHistory={openHistory}
        onOpenTemplates={openTemplates}
        onOpenBrand={() => setShowBrandDialog(true)}
        onOpenSignup={openSignup}
        onOpenLogin={openLogin}
        onReset={resetAll}
        hasBrand={hasBrandData(brand)}
      />

      <main className="relative z-10 px-4 md:px-10 py-6 md:py-8 max-w-[1600px] mx-auto">
        <HeroUpload onFile={handleExtract} loading={extracting} />

        {(cases.length > 0 || vehicles.length > 0) && (
          <CargoAndFleetSection
            cases={cases}
            setCases={setCases}
            vehicles={vehicles}
            setVehicles={setVehicles}
            summary={summary}
            onSaveTemplate={handleSaveTemplate}
            sourceDimUnit={sourceUnits.dim}
            sourceWeightUnit={sourceUnits.weight}
          />
        )}

        {cases.length > 0 && (
          <section className="mb-6 fade-up">
            <TripDetails trip={trip} onChange={setTrip} />
          </section>
        )}

        {cases.length > 0 && (
          <CalculateBar
            jobName={jobName}
            onJobNameChange={setJobName}
            onCalculate={handleCalculate}
            calculating={calculating}
            optimizeMinTrucks={optimizeMinTrucks}
            onToggleOptimize={() => setOptimizeMinTrucks((v) => !v)}
          />
        )}

        {result && (
          <>
            {!hasBrandData(brand) && (
              <BrandNudgeBanner
                user={user}
                onOpenBrand={() => setShowBrandDialog(true)}
                onLogin={openLogin}
                onSignup={openSignup}
              />
            )}
            <ResultSection
              result={result}
              tripSheet={<TripSheet result={result} vehicles={vehicles} trip={trip} />}
              dimUnit={dimUnit}
              onDimUnitChange={handleDimUnitChange}
              weightUnit={weightUnit}
              onWeightUnitChange={handleWeightUnitChange}
              tableLayout={tableLayout}
              onTableLayoutChange={handleTableLayoutChange}
              coverEnabled={coverPage?.enabled}
              onOpenCover={() => setShowCoverDialog(true)}
              onSave={handleSave}
              onDownload={handleDownload}
              onDownloadPdf={handleDownloadPdf}
              onShare={handleShare}
              onOpenBrand={() => setShowBrandDialog(true)}
            />
          </>
        )}
      </main>

      <HistoryDrawer
        show={showHistory}
        refreshKey={historyRefreshKey}
        templatesRefreshKey={templatesRefreshKey}
        initialTab={drawerTab}
        onClose={() => setShowHistory(false)}
        onLoad={loadFromHistory}
        onLoadTemplate={loadFromTemplate}
      />

      <BrandSettingsDialog
        show={showBrandDialog}
        brand={brand}
        onSave={handleSaveBrand}
        onClose={() => setShowBrandDialog(false)}
      />

      <CoverPageDialog
        show={showCoverDialog}
        cover={coverPage}
        onSave={handleSaveCover}
        onClose={() => setShowCoverDialog(false)}
      />

      <AuthDialog
        show={authDialog.show}
        initialTab={authDialog.tab}
        onClose={closeAuth}
        onFirstSignup={handleFirstSignup}
      />

      <OnboardingDialog
        show={showOnboarding}
        initialBrand={brand}
        onSave={finishOnboarding}
        onSkip={skipOnboarding}
      />

      <footer className="mt-16 border-t border-slate-200 py-6 px-6 md:px-10">
        <div className="max-w-[1600px] mx-auto flex flex-col md:flex-row items-center justify-between gap-2">
          <div className="mono text-xs text-[#64748B]">
            CARGOLOAD.PRO — Built for transporters. Data stays in your workspace.
          </div>
          <div className="overline">v1.0</div>
        </div>
      </footer>
    </div>
  );
}
